/**
 * Confirm Context
 * 앱 전역에서 사용할 수 있는 확인 다이얼로그 시스템
 * window.confirm() 대체용
 */

import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from "react";

interface ConfirmOptions {
  title: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "default";
}

interface ConfirmState extends ConfirmOptions {
  isOpen: boolean;
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  confirmState: ConfirmState;
  handleConfirm: () => void;
  handleCancel: () => void;
}

const initialState: ConfirmState = {
  isOpen: false,
  title: "",
  message: "",
  confirmText: "확인",
  cancelText: "취소",
  variant: "default",
};

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [confirmState, setConfirmState] = useState<ConfirmState>(initialState);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setConfirmState({
        isOpen: true,
        title: options.title,
        message: options.message || "",
        confirmText: options.confirmText || "확인",
        cancelText: options.cancelText || "취소",
        variant: options.variant || "default",
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    resolveRef.current?.(true);
    resolveRef.current = null;
    setConfirmState(initialState);
  }, []);

  const handleCancel = useCallback(() => {
    resolveRef.current?.(false);
    resolveRef.current = null;
    setConfirmState(initialState);
  }, []);

  return (
    <ConfirmContext.Provider value={{ confirm, confirmState, handleConfirm, handleCancel }}>
      {children}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirm must be used within a ConfirmProvider");
  }
  return context.confirm;
}

export function useConfirmDialog() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirmDialog must be used within a ConfirmProvider");
  }
  return context;
}
